import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Button,
  Tooltip as MuiTooltip,
} from '@mui/material';
import {
  Dashboard,
  TrendingUp,
  People,
  Restaurant,
  ShoppingCart,
  Assessment,
  Map,
  CloudDownload,
  CheckCircle,
  Handshake,
  MoneyOff,
  DocumentScanner,
  Gavel,
  Refresh,
  Accessibility,
  Accessible,
  SmartToy,
  Notifications,
  PointOfSale,
} from '@mui/icons-material';
import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/auth';
import axios from 'axios';
import { RegionProvider, useRegionContext } from '../region/RegionContext';
import CountryTabs from '../components/CountryTabs';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

// Import existing components
import AdminClaimReview from './AdminClaimReview';
import NewBusinesses from './AdminDashboard/NewBusinesses';
import Verified from './AdminDashboard/Verified';
import Partners from './AdminDashboard/Partners';
import VenueMap from './AdminDashboard/VenueMap';
import UserMap from './AdminDashboard/UserMap';
import ImportedVenues from './AdminDashboard/ImportedVenues';
import ClaimsEngagementTab from './AdminDashboard/ClaimsEngagementTab';
import RefundTracking from './AdminDashboard/RefundTracking';
import CvMenuReview from './AdminDashboard/CvMenuReview';
import AltTextReview from './AdminDashboard/AltTextReview';
import UsersTab from './AdminDashboard/UsersTab';
import OrdersTab from './AdminDashboard/OrdersTab';
import TabDescription from './AdminDashboard/TabDescription';
import AiAnalyticsTab from './AdminDashboard/AiAnalyticsTab';
import AccessibilityAnalyticsTab from './AdminDashboard/AccessibilityAnalyticsTab';
import ActivityFeedTab from './AdminDashboard/ActivityFeedTab';
import POSConnectionsTab from './AdminDashboard/POSConnectionsTab';
import { BusinessDataProvider } from '../context/BusinessDataContext';
import { queryAllRegions } from '../utils/queryAllRegions';

const AdminDashboard = () => {
  const savedRegion = localStorage.getItem('piatto_admin_region') || 'AU';

  return (
    <RegionProvider initialRegion={savedRegion}>
      <BusinessDataProvider>
        <AdminDashboardContent />
      </BusinessDataProvider>
    </RegionProvider>
  );
};

// Re-export so existing tab imports (from '../AdminDashboard') keep working.
export { queryAllRegions };

const AdminDashboardContent = () => {
  const { db, endpoints, activeRegion, setActiveRegion, isGlobal, allRegionDbs, allEndpoints, loading: regionLoading } = useRegionContext();
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminRegions, setAdminRegions] = useState(null); // null = unrestricted, array = restricted

  const [recalculating, setRecalculating] = useState(false);

  // Analytics state
  const [dateRange, setDateRange] = useState('30days');

  // Data state
  const [analyticsData, setAnalyticsData] = useState({
    ordersOverTime: [],
    usersByLocation: [],
    totalVenues: 0,
    totalUsers: 0,
    totalOrders: 0,
    activeVenues: 0,
    newBusinesses: [],
    verifiedPartners: [],
  });

  useEffect(() => {
    document.title = "Admin Dashboard | Piatto";
  }, []);

  useEffect(() => {
    checkAdminAccess();
  }, []);

  useEffect(() => {
    if (isAdmin && db) {
      loadAnalyticsData();
    }
  }, [isAdmin, db, isGlobal, allRegionDbs, dateRange]);

  const checkAdminAccess = async () => {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        setError('Please log in to access the admin dashboard');
        setLoading(false);
        return;
      }

      const idTokenResult = await user.getIdTokenResult();
      if (!idTokenResult.claims.admin) {
        setError('You do not have admin privileges');
        setLoading(false);
        return;
      }

      // adminRegions claim: array of country codes this admin can access.
      // If absent/null, the admin has unrestricted (global) access.
      const regions = idTokenResult.claims.adminRegions || null;
      setAdminRegions(regions);

      setIsAdmin(true);
      setLoading(false);
    } catch (err) {
      console.error('Error checking admin access:', err);
      setError('Failed to verify admin access');
      setLoading(false);
    }
  };

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      if (dateRange === '7days') startDate.setDate(endDate.getDate() - 7);
      else if (dateRange === '30days') startDate.setDate(endDate.getDate() - 30);
      else if (dateRange === '90days') startDate.setDate(endDate.getDate() - 90);
      else if (dateRange === 'year') startDate.setFullYear(endDate.getFullYear() - 1);

      let orders, totalVenues = 0, totalUsers = 0, totalOrders = 0, activeVenues = 0;

      if (isGlobal && allRegionDbs) {
        // Global mode: only read _stats/summary from each region (1 read per region)
        // and a small Orders query for the chart
        const [allStats, allOrders] = await Promise.all([
          Promise.allSettled(
            Object.entries(allRegionDbs).map(async ([rc, regionDb]) => {
              const doc = await regionDb.doc('_stats/summary').get();
              return { rc, data: doc.exists ? doc.data() : null };
            })
          ),
          queryAllRegions(allRegionDbs, null, (regionDb) =>
            regionDb.collection('Orders')
              .where('timestamp', '>=', firebase.firestore.Timestamp.fromDate(startDate))
              .limit(200)
          ),
        ]);

        orders = allOrders;

        // Sum stats across all regions
        allStats.forEach((r) => {
          if (r.status === 'fulfilled' && r.value.data) {
            const s = r.value.data;
            totalVenues += s.totalVenues || 0;
            totalUsers += s.totalUsers || 0;
            totalOrders += s.totalOrders || 0;
            activeVenues += s.activeVenues || 0;
          }
        });
      } else {
        // Single-region mode: only read _stats/summary + Orders for the chart
        const [statsResult, ordersResult] = await Promise.allSettled([
          db.doc('_stats/summary').get(),
          db.collection('Orders')
            .where('timestamp', '>=', firebase.firestore.Timestamp.fromDate(startDate))
            .limit(200)
            .get(),
        ]);

        const statsData = statsResult.status === 'fulfilled' && statsResult.value.exists
          ? statsResult.value.data() : null;
        totalVenues = statsData?.totalVenues ?? 0;
        totalUsers = statsData?.totalUsers ?? 0;
        totalOrders = statsData?.totalOrders ?? 0;
        activeVenues = statsData?.activeVenues ?? 0;

        orders = ordersResult.status === 'fulfilled'
          ? ordersResult.value.docs.map(doc => ({ id: doc.id, ...doc.data() })) : [];
      }

      setAnalyticsData({
        ordersOverTime:   processOrdersOverTime(orders, startDate, endDate),
        usersByLocation:  [], // loaded on-demand by User Map tab to save reads
        totalVenues,
        totalUsers,
        totalOrders:      totalOrders || orders.length,
        activeVenues,
        newBusinesses:    [], // loaded on-demand by the New Businesses tab
        verifiedPartners: [], // loaded on-demand by the Partners tab
      });
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError('Failed to load analytics data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      await loadAnalyticsData();
    } catch (err) {
      console.error('Recalculate error:', err);
    } finally {
      setRecalculating(false);
    }
  };

  const processOrdersOverTime = (orders, startDate, endDate) => {
    const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    const data = [];

    for (let i = 0; i < days; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      const dateStr = date.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });

      const dayOrders = orders.filter(order => {
        const orderDate = order.timestamp?.toDate();
        return orderDate && orderDate.toDateString() === date.toDateString();
      });

      data.push({
        date: dateStr,
        orders: dayOrders.length,
      });
    }

    return data;
  };

  // Users by Location chart removed — data is available on the User Map tab.
  // This avoids loading all Users docs (5000+) just for a summary bar chart.

  const AnalyticsTab = () => (
    <Box>
      <TabDescription>
        High-level platform metrics pulled from the Business, Users, and Orders Firestore collections.
        Use the filters to drill down by location or time period. Summary totals come from a lightweight
        _stats/summary counter doc — hit "Refresh Stats" to trigger a server-side recount if numbers look stale.
        Monitor order trends and user distribution to guide marketing and expansion decisions.
      </TabDescription>
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  label="Date Range"
                >
                  <MenuItem value="7days">Last 7 Days</MenuItem>
                  <MenuItem value="30days">Last 30 Days</MenuItem>
                  <MenuItem value="90days">Last 90 Days</MenuItem>
                  <MenuItem value="year">Last Year</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <MuiTooltip title="Runs a server-side count query to get accurate totals without downloading all documents">
          <Button
            size="small"
            variant="outlined"
            startIcon={recalculating ? <CircularProgress size={14} /> : <Refresh />}
            onClick={handleRecalculate}
            disabled={recalculating}
          >
            {recalculating ? 'Recalculating…' : 'Refresh Stats'}
          </Button>
        </MuiTooltip>
      </Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Restaurant sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Total Venues
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {analyticsData.totalVenues}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <People sx={{ fontSize: 40, color: 'success.main' }} />
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Total Users
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {analyticsData.totalUsers}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ShoppingCart sx={{ fontSize: 40, color: 'warning.main' }} />
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Total Orders
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {analyticsData.totalOrders}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUp sx={{ fontSize: 40, color: 'info.main' }} />
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Active Venues
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {analyticsData.activeVenues}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Orders Over Time
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={analyticsData.ordersOverTime}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="orders"
                    stroke="#B91C1C"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Users by Location
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  See the User Map tab for geographic user distribution.
                  <br />
                  Counts shown above come from lightweight stats docs.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  if (loading || regionLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 8, textAlign: 'center' }} role="status" aria-live="polite">
        <CircularProgress size={60} aria-label="Loading" />
        <Typography variant="h6" sx={{ mt: 2 }}>
          {regionLoading ? `Loading ${activeRegion} region...` : 'Loading admin dashboard...'}
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 8, mb: 8 }} component="main">
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Dashboard sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700 }}>
            Piatto Admin Dashboard
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary">
          Global analytics, venue management, and claim reviews
        </Typography>
      </Box>

      {/* Country tabs — auto-populated from deployed regions */}
      <CountryTabs activeRegion={activeRegion} onRegionChange={setActiveRegion} adminRegions={adminRegions} />

      <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<Notifications />} label="Activity Feed" />
          <Tab icon={<Assessment />} label="Analytics" />
          <Tab icon={<Restaurant />} label="New Businesses" />
          <Tab icon={<CheckCircle />} label="Verified" />
          <Tab icon={<TrendingUp />} label="Partners" />
          <Tab icon={<PointOfSale />} label="POS Connections" />
          <Tab icon={<People />} label="Users" />
          <Tab icon={<ShoppingCart />} label="Orders" />
          <Tab icon={<CloudDownload />} label="Imported Venues" />
          <Tab icon={<Map />} label="Venue Map" />
          <Tab icon={<Map />} label="User Map" />
          <Tab icon={<Gavel />} label="Claim Reviews" />
          <Tab icon={<Handshake />} label="Claims & Engagement" />
          <Tab icon={<MoneyOff />} label="Refund Tracking" />
          <Tab icon={<DocumentScanner />} label="CV Menus" />
          <Tab icon={<Accessibility />} label="Alt Text" />
          <Tab icon={<SmartToy />} label="AI Analytics" />
          <Tab icon={<Accessible />} label="Accessibility Analytics" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {currentTab === 0 && <ActivityFeedTab />}
          {currentTab === 1 && <AnalyticsTab />}
          {currentTab === 2 && <NewBusinesses />}
          {currentTab === 3 && <Verified />}
          {currentTab === 4 && <Partners />}
          {currentTab === 5 && <POSConnectionsTab />}
          {currentTab === 6 && <UsersTab />}
          {currentTab === 7 && <OrdersTab />}
          {currentTab === 8 && <ImportedVenues />}
          {currentTab === 9 && <VenueMap />}
          {currentTab === 10 && <UserMap />}
          {currentTab === 11 && <AdminClaimReview />}
          {currentTab === 12 && <ClaimsEngagementTab />}
          {currentTab === 13 && <RefundTracking />}
          {currentTab === 14 && <CvMenuReview />}
          {currentTab === 15 && <AltTextReview />}
          {currentTab === 16 && <AiAnalyticsTab />}
          {currentTab === 17 && <AccessibilityAnalyticsTab />}
        </Box>
      </Card>
    </Container>
  );
};

export default AdminDashboard;
